import { useState, useMemo } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { PRODUCTS } from "@/data/products";
import { Plus, Check } from "lucide-react";

const CATEGORY_KEYS = ["all", "empanadas", "dulces", "salados", "bebidas"];

const Tienda = () => {
  const { t, lang } = useLang();
  const { addItem } = useCart();
  const [active, setActive] = useState("all");
  const [justAdded, setJustAdded] = useState(null);

  const filtered = useMemo(() => {
    if (active === "all") return PRODUCTS;
    return PRODUCTS.filter((p) => p.category === active);
  }, [active]);

  const handleAdd = (p) => {
    addItem({
      id: p.id,
      name: p.name,
      price: p.price,
      unit: p.unit,
      image: p.image,
    });
    setJustAdded(p.id);
    setTimeout(() => setJustAdded(null), 1500);
  };

  return (
    <div data-testid="page-tienda">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-12">
        <div className="max-w-[1500px] mx-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-caramel mb-6 font-medium">
            {t.shop.overline}
          </div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-ink">
            {t.shop.title}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-ink-muted font-light max-w-2xl leading-relaxed">
            {t.shop.sub}
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-20 md:top-24 z-30 bg-bg/95 backdrop-blur-md border-y border-bone">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-5 overflow-x-auto">
          <div className="flex gap-8 md:gap-10 whitespace-nowrap">
            {CATEGORY_KEYS.map((cat, idx) => (
              <button
                key={cat}
                data-testid={`shop-filter-${cat}`}
                onClick={() => setActive(cat)}
                className={`text-[11px] uppercase tracking-[0.25em] font-medium transition-colors pb-1 border-b ${
                  active === cat ? "text-wine border-wine" : "text-ink-muted border-transparent hover:text-ink"
                }`}
              >
                {t.products.filters[idx]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {filtered.map((p) => (
              <article
                key={p.id}
                data-testid={`shop-product-${p.id}`}
                className="group flex flex-col"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-surface">
                  <img
                    src={p.image}
                    alt={p.name[lang]}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="mt-5 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-caramel font-medium mb-2">
                    {p.category}
                  </div>
                  <h3 className="font-serif text-2xl text-ink font-light tracking-tight leading-tight">
                    {p.name[lang] || p.name.es}
                  </h3>
                  <p className="text-sm text-ink-muted font-light mt-3 line-clamp-2 flex-1">
                    {p.description[lang] || p.description.es}
                  </p>
                  <div className="mt-5 pt-5 border-t border-bone flex items-center justify-end gap-4">
                    <button
                      data-testid={`add-to-cart-${p.id}`}
                      onClick={() => handleAdd(p)}
                      className={`inline-flex items-center gap-2 px-5 py-3 text-[10px] uppercase tracking-[0.2em] font-medium transition-all ${
                        justAdded === p.id
                          ? "bg-caramel text-bg"
                          : "bg-ink text-bg hover:bg-wine"
                      }`}
                    >
                      {justAdded === p.id ? (
                        <>
                          <Check className="w-3.5 h-3.5" strokeWidth={2} />
                          {t.shop.added}
                        </>
                      ) : (
                        <>
                          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                          {t.common.addToCart}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tienda;
